import * as searchActions from './actions';
import * as lastStateActions from '../lastState/actions';
import {
  getSearchApiParams,
  getSearchOptions,
  getSearchText,
  getSearchUploadData,
} from './selectors';
import { getAssetLabLogger } from '../app/logs';
import { takeEvery, takeLatest } from '../../listenerMiddleware';
import * as api from '../../api';
import { message } from '@myua/kit';
import { showNonLatinWarning } from './components/showNonLatinWarning';

export const searchListeners = () => {
  watchSearchChanged();
  watchLastState();
  watchSimilarAssetChanged();
};

function watchSearchChanged() {
  takeEvery(
    [searchActions.queryChanged, searchActions.optionsChanged],
    (action, { getState, getOriginalState, dispatch }) => {
      const assetLabLogger = getAssetLabLogger(getState());
      const text = getSearchText(getState());

      if (searchActions.optionsChanged.match(action)) {
        if (action.payload.aiToggleClicked) {
          assetLabLogger({
            action: 'Filter',
            sub_action: 'Change AI search mode',
            extra: {
              bool_1: action.payload.options.searchWithAi,
              context: 'icon',
            },
          });
        } else {
          const previous = getSearchOptions(getOriginalState());
          if (previous.searchWithAi !== action.payload.options.searchWithAi) {
            assetLabLogger({
              action: 'Filter',
              sub_action: 'Change AI search mode',
              extra: {
                bool_1: action.payload.options.searchWithAi,
                context: 'options',
              },
            });
          }
        }
        if (!text) {
          // Если юзер меняет опции, когда в строке поиска ничего не задано,
          // то нет смысла еще раз делать запрос
          return;
        }
      }

      const options = getSearchOptions(getState());
      if (options.searchWithAi && isNonLatin(text)) {
        showNonLatinWarning();
      }

      dispatch(searchActions.searchRequested());

      const apiParams = getSearchApiParams(getState());
      const uploadData = getSearchUploadData(getState());

      assetLabLogger({
        action: 'Filter',
        sub_action: 'Search by text',
        extra: {
          text_1: apiParams.search ?? '',
          bool_1: Boolean(apiParams.search_in_layers),
          bool_2: Boolean(apiParams.search_in_archives),
          bool_3: Boolean(apiParams.search_with_ai),
          int_1: apiParams.search_upload_id ?? null,
          int_2:
            apiParams.search_upload_id !== undefined && uploadData?.similarAssetId
              ? uploadData.similarAssetId
              : null,
          text_2:
            apiParams.search_upload_id !== undefined && uploadData ? uploadData.fileName : null,
        },
      });
    },
  );
}

function watchLastState() {
  takeLatest(searchActions.searchRequested, (action, { dispatch }) => {
    dispatch(lastStateActions.saveRequested());
  });
}

function watchSimilarAssetChanged() {
  takeLatest(searchActions.similarAssetChanged, async (action, { dispatch, signal }) => {
    if (!action.payload.similarAsset) {
      return;
    }

    const result = await api.getSearchUploadIdForSimilar(action.payload.similarAsset.id);

    if (signal.aborted) {
      return;
    }

    if (result.success) {
      dispatch(
        searchActions.queryChanged({
          text: '',
          uploadData: {
            id: result.data.response.search_upload_id,
            fileName: action.payload.similarAsset.name,
            similarAssetId: action.payload.similarAsset.id,
          },
        }),
      );
    } else {
      void message.error('Failed to get the search ID for similar assets');
    }

    dispatch(searchActions.similarAssetChanged({ similarAsset: undefined }));
  });
}

function isNonLatin(text: string): boolean {
  // Определяет наличие букв любого алфавита, кроме латиницы.
  // Цифры и спецсимволы не блокируются.
  const NON_LATIN_LETTERS = /[^\P{L}a-zA-Z]/u;

  return NON_LATIN_LETTERS.test(text);
}
